import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { generateRandomColor } from '../../utils/colorUtils';
import { HOSTNAME } from "../../configs/Domain";
import { sampleModernColors } from "../../utils/colors";
import { COOL_COLOR_PALETTE } from "../../utils/colorUtils";
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_START = '2019-01-03';

const comparisonInitialState = {
    items: [],
    commonStartDate: DEFAULT_START,
    commonEndDate: null,
    commonBenchmark: null,
    needRefetch: false,
    loading: false,
    error: null,
    itemColors: {},
    badItems: [],
    status: 'idle',
    showSearch: true,
    refireAttempts: 0,
    history: [], // Array of {id: uuid, timestamp: Date, items: [], itemColors: {}}
}

// Helper function to create history entry
const createHistoryEntry = (items, itemColors) => ({
    id: uuidv4(),
    timestamp: new Date().toISOString(),
    items: JSON.parse(JSON.stringify(items)), // Deep clone to prevent reference issues
    itemColors: { ...itemColors }
});

const fetchTimeSeriesData = createAsyncThunk(
    'comparison/fetchTimeSeriesData',
    async ({ symbols, startDate = DEFAULT_START, endDate, benchmark, labels }, { getState, rejectWithValue }) => {
        const state = getState().comparison;
        if (state.loading && state.refireAttempts > 1) return rejectWithValue('Fetch in progress');
        let url = `${HOSTNAME}/performance/compare?symbols=${symbols.join(',')}`;
        url += `&sd=${startDate}`;
        if (endDate) url += `&ed=${endDate}`;
        if (benchmark) url += `&benchmark=${benchmark}`;

        try {
            const response = await axios.get(url);
            const data = response.data;
            return { symbols, startDate, endDate, benchmark, data, labels };
        } catch (error) {
            return rejectWithValue(error.toString());
        }
    }
);

// Thunk to re-fetch all items with new common start date
export const refetchAllItems = createAsyncThunk(
    'comparison/refetchAllItems',
    async (_, { getState, dispatch }) => {
        const state = getState().comparison;
        const { items, commonStartDate } = state;
        for (const item of items) {
            await dispatch(fetchTimeSeriesData({ ...item, startDate: commonStartDate }));
        }
    }
);

const comparisonSlice = createSlice({
    name: 'comparison',
    initialState: comparisonInitialState,
    reducers: {
        removeItem: (state, action) => {
            const id = action.payload.id;
            // Save current state to history before removing item
            state.history.push(createHistoryEntry(state.items, state.itemColors));

            state.items = state.items.filter(item => item.id !== id);
            delete state.itemColors[id];
            state.badItems = state.badItems.filter(item => item.id !== id);
        },
        resetComparison: (state) => {
            // Save current state to history before resetting
            if (state.items.length > 0) {
                state.history.push(createHistoryEntry(state.items, state.itemColors));
            }

            state.items = [];
            state.commonStartDate = DEFAULT_START;
            state.commonEndDate = null;
            state.needRefetch = false;
            state.loading = false;
            state.itemColors = {};
            state.showSearch = true; // Always show search on reset

        },
        removeHistoryEntry: (state, action) => {
            const historyId = action.payload;
            state.history = state.history.filter(entry => entry.id !== historyId);
        },
        restoreFromHistory: (state, action) => {
                    const historyId = action.payload;
                    const historyEntry = state.history.find(entry => entry.id === historyId);

                    if (historyEntry) {
                        // Save current state if we have items and this state isn't already in history
                        if (state.items.length > 0) {
                            const isCurrentStateInHistory = state.history.some(entry => {
                                // Check if this exact state is already saved
                                // Compare items length and first item to determine if it's likely the same state
                                return entry.items.length === state.items.length &&
                                       entry.items[0]?.id === state.items[0]?.id &&
                                       entry.timestamp > historyEntry.timestamp;
                            });

                            if (!isCurrentStateInHistory) {
                                state.history.push(createHistoryEntry(state.items, state.itemColors));
                            }
                        }

                        // Now restore the selected history entry
                        state.items = JSON.parse(JSON.stringify(historyEntry.items));
                        state.itemColors = { ...historyEntry.itemColors };
                        state.showSearch = false; // Hide search when restoring items
                    }
        },
        clearHistory: (state) => {
            state.history = [];
        },
        setCommonBenchmark: (state, action) => {
            state.commonBenchmark = action.payload;
        },
        updateItemColor: (state, action) => {
            const { id, color } = action.payload;
            if (state.itemColors[id]) {
                state.itemColors[id] = color;
            }
        },
        setShowSearch: (state, action) => {
            state.showSearch = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTimeSeriesData.pending, (state) => {
                if (state.status !== 'pending') {
                    state.status = 'pending';
                    state.loading = true;
                    state.refireAttempts += 1;
                }
            })
            .addCase(fetchTimeSeriesData.fulfilled, (state, action) => {
                const { symbols, startDate, data, labels } = action.payload;

                // Save current state to history before updating
                if (state.items.length > 0) {
                    state.history.push(createHistoryEntry(state.items, state.itemColors));
                }

                state.loading = false;
                state.status = 'idle';
                state.refireAttempts = 0;

                symbols.forEach(symbol => {
                    const index = state.items.findIndex(item => item.id === symbol);
                    const itemData = data.find(item => item.id === symbol);
                    let itemLabel = symbol.toUpperCase();

                    if (index === -1) {
                        const currentItems = state.items.length;
                        const newColor = currentItems < COOL_COLOR_PALETTE.length
                            ? COOL_COLOR_PALETTE[currentItems]
                            : sampleModernColors(1)[0];

                        if (labels) {
                            const label = labels.find(label => label.id === symbol);
                            if (label) itemLabel = label.label;
                        }

                        state.items.push({
                            id: symbol,
                            startDate,
                            ...itemData.data,
                            label: itemLabel,
                            needsUpdate: false,
                            color: newColor
                        });
                        state.itemColors[symbol] = newColor;
                    } else {
                        state.items[index] = {
                            ...state.items[index],
                            startDate,
                            ...itemData.data,
                            needsUpdate: false,
                            label: itemLabel
                        };
                    }
                });
            })
            .addCase(fetchTimeSeriesData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            });
    }
});

export const { reducer: comparisonReducer } = comparisonSlice;

export const comparisonActions = {
    ...comparisonSlice.actions,
    fetchTimeSeriesData,
    refetchAllItems
};

// Action creator exports for direct use
export const {
    removeItem,
    resetComparison,
    removeHistoryEntry,
    restoreFromHistory,
    clearHistory,
    setCommonBenchmark,
    updateItemColor,
    setShowSearch
} = comparisonSlice.actions;